<template>
    <div v-if="exchangeStore.loading" class="flex min-h-[70vh] items-center justify-center"><Loader /></div>
    <div v-else class="ds-exchange-content">
        <VacanciesToolbar :archive="true" :active-filters-count="activeFiltersCount" @search-input="onSearch" @change-view="onChangeView" @global-filter-click="toggleGlobalFilter" />
        <div v-if="isGlobalFilterOpen" class="relative">
            <div class="absolute right-0 top-2 z-20" v-click-outside="closeGlobalFilter">
                <VacanciesFilter
                    global
                    :positions-items="filterPositionsItems"
                    :departments-items="filterDepartmentsItems"
                    :statuses-items="filterStatusesItems"
                    :date-range="filterDate"
                    @update:positions-items="filterPositionsItems = $event"
                    @update:departments-items="filterDepartmentsItems = $event"
                    @update:statuses-items="filterStatusesItems = $event"
                    @update:date-range="filterDate = $event"
                    @check="onGlobalFilterCheck"
                    @reset="allFiltersReset"
                />
            </div>
        </div>
        <div v-if="gridView" class="pt-7">
            <div class="wrapper overflow-y-auto ds-exchange-grid">
                <template v-if="exchangeStore.vacancyArchiveCount">
                    <div v-for="vacancy in vacancies" :key="vacancy.id" class="ds-exchange-grid-item">
                        <VacancyArchiveGridItem :vacancy @view="onViewVacancy(vacancy)" />
                    </div>
                </template>
                <div v-else class="col-span-full text-center pt-28 text-lg">Ничего не найдено</div>
            </div>
        </div>
        <!-- list view begin-->
        <div v-else>
            <div class="ds-exchange-table-header ds-exchange-list-grid pt-6 pb-1" >
                <div class="w-8"></div>
                <div class="ds-exchange-col-date flex relative">
                    <span class="cursor-pointer" @click="toggleOrdering">Дата и время</span>
                    <DateRangeFilter v-model="filterDate" @selected="onColumnFilterCheck" />
                </div>
                <div class="ds-exchange-col-position flex relative">Должность
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('positions')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'positions'" v-model="filterPositionsItems" width="320px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @check="onColumnFilterCheck" @reset="onFilterPositionReset" />
                </div>
                <div class="ds-exchange-col-department flex relative">Подразделение
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('departments')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'departments'" v-model="filterDepartmentsItems" width="240px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @check="onColumnFilterCheck" @reset="onFilterDepartmentReset" />
                </div>
                <div class="ds-exchange-col-status flex relative">Статус
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('statuses')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'statuses'" v-model="filterStatusesItems" width="180px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @check="onColumnFilterCheck" @reset="onFilterStatusesReset" />
                </div>
                <div class="ds-exchange-col-employee">Сотрудник</div>
            </div>
            <div class="wrapper overflow-y-auto text-sm">
                <template v-if="exchangeStore.vacancyArchiveCount">
                    <div v-for="vacancy in vacancies" :key="vacancy.id" class="ds-exchange-table-row ds-exchange-list-grid">
                        <VacancyArchiveListItem :vacancy @view="onViewVacancy(vacancy)" />
                    </div>
                </template>
                <div v-else class="text-center pt-28 text-lg">Ничего не найдено</div>
            </div>
        </div>
        <!-- list view end-->
        <Pager :items="exchangeStore.vacancyArchiveCount" @page="onPage" />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import VacancyArchiveListItem from './VacancyArchiveListItem.vue'
import VacancyArchiveGridItem from './VacancyArchiveGridItem.vue'
import Pager from './Pager.vue'
import VacanciesToolbar from './VacanciesToolbar.vue'
import VacanciesFilter from './VacanciesFilter.vue'
import DateRangeFilter from './DateRangeFilter.vue'
import IconSort from '@/ui/icons/IconSort.vue'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import Loader from '@/ui/Loader.vue'
import debounce from 'lodash.debounce'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useExchangeStore } from '@/stores/exchangeStore'

const scheduleStore = useScheduleStore()
const exchangeStore = useExchangeStore()

const emit = defineEmits(['user-click', 'check-click', 'create', 'view'])
const gridView = ref(false)
const searchText = ref('')
const filterPositionsItems = ref<any[]>([])
const filterDepartmentsItems = ref<any[]>([])
const filterStatusesItems = ref<any[]>([])
const filterDate = ref<[Dayjs, Dayjs] | null>(null)
const vacanciesPerPage = ref(15)
const pageNum = ref(1)
const ordering = ref(false)
const isGlobalFilterOpen = ref(false)
const openFilter = ref<'positions' | 'departments' | 'statuses' | null>(null)

const vacancies = computed(() => exchangeStore.vacancyArchive)
const activeFiltersCount = computed(() => (
    filterPositionsItems.value.filter(item => item.check).length
    + filterDepartmentsItems.value.filter(item => item.check).length
    + filterStatusesItems.value.filter(item => item.check).length
    + (filterDate.value ? 1 : 0)
))

const onSearch = debounce((text: string) => {
    searchText.value = text
    console.log(searchText.value)
    getVacancies()
}, 500) 

const onPage = async (data:any) => {
    const { page, itemsPerPage } = data
    pageNum.value = page
    vacanciesPerPage.value = itemsPerPage.id
    await getVacancies()
}

const onViewVacancy = (vacancy: any) => {
    emit('view', vacancy)
}

const onChangeView = (grid: boolean) => {
    gridView.value = grid
}

const onFilterPositionReset = () => {
    filterPositionsItems.value = scheduleStore.positions.map((item:any) => { return {check: false, ...item} } )
}

const onFilterDepartmentReset = () => {
    filterDepartmentsItems.value = [...new Set(vacancies.value.map((item:any) => item.department.name ))].map(item => {return { check:false, name: item }})
}

const onFilterStatusesReset = () => {
    filterStatusesItems.value = exchangeStore.vacancyStatuses.map((item:any) => { return {check: false, ...item} } )
}

const toggleOrdering = () => {
    ordering.value = !ordering.value
    getVacancies()
}

const allFiltersReset = async () => {
    onFilterPositionReset()
    filterDate.value = null
    onFilterDepartmentReset()
    onFilterStatusesReset()
    pageNum.value = 1
    await getVacancies()
}

const closeColumnFilter = () => { openFilter.value = null }
const toggleFilter = (filter: 'positions' | 'departments' | 'statuses') => {
    openFilter.value = openFilter.value === filter ? null : filter
}
const toggleGlobalFilter = () => {
    closeColumnFilter()
    isGlobalFilterOpen.value = !isGlobalFilterOpen.value
}
const closeGlobalFilter = () => { isGlobalFilterOpen.value = false }
const onGlobalFilterCheck = async () => {
    pageNum.value = 1
    await getVacancies()
}
const onColumnFilterCheck = async () => {
    pageNum.value = 1
    await getVacancies()
}

async function getVacancies() {
    const order = ordering.value ? 'dttm_work_start_at' : '-dttm_work_start_at'
    const data:any = {
        page_size: vacanciesPerPage.value,
        page: pageNum.value,
        ordering: order
    }
    if(filterDepartmentsItems.value.filter(item => item.check).length) data.department = filterDepartmentsItems.value.filter(item => item.check).map(item => item.id).join(',')
    if(filterPositionsItems.value.filter(item => item.check).length) data.position = filterPositionsItems.value.filter(item => item.check).map(item => item.id).join(',')
    if(filterStatusesItems.value.filter(item => item.check).length) data.status = filterStatusesItems.value.filter(item => item.check).map(item => item.value).join(',')
    if(filterDate.value) {
        data.dt_from = dayjs(filterDate.value[0]).format('YYYY-MM-DD')
        data.dt_to = dayjs(filterDate.value[1]).format('YYYY-MM-DD')
    }
    if(searchText.value) data.search = searchText.value
    await exchangeStore.fetchVacancyArchive(data)
}

onMounted(async () => {
    await getVacancies()
    onFilterDepartmentReset()
    onFilterStatusesReset()
    onFilterPositionReset()
})
</script>

<style>
.wrapper {
    height: calc(100vh - 300px)
}
</style>
